<template>
  <div>
    <ProgressBar v-if="loading" mode="indeterminate" style="height: 0.2em" />
    <div v-else>
      <Toolbar class="p-0" v-if="store.getters.info.isAdmin">
        <template #start>
          <Button title="Новая" icon="pi pi-plus" class="m-1" @click="addNewTemplate" />
          <Button title="Редактировать" icon="pi pi-pencil" class="m-1" @click="editTemplate" :disabled="active < 0" />
          <Button title="Удалить" icon="pi pi-trash" class="m-1" @click="deleteDialog = true" :disabled="active < 0" />
          <Button title="Дизайн" icon="pi pi-cog" class="m-1" @click="onDesignReport" :disabled="active < 0" />
        </template>
      </Toolbar>
      <div class="p-tabmenu p-component">
        <ul class="p-tabmenu-nav p-reset" role="tablist">
          <li :class="['p-tabmenuitem', active === index ? 'p-highlight' : '']" v-for="(item, index) in template"
            :key="index" @click="onClickMenu(index)">
            <a class="p-menuitem-link" :style="{ height: '1rem' }">
              <span class="p-menuitem-icon pi pi-fw pi-file"></span>
              <span class="p-menuitem-text">{{ item.report_template_name }}</span>
            </a>
          </li>
        </ul>
      </div>
    </div>
    <ScrollPanel :style="{ height: scrollHeight + 'px' }">
      <div id="viewer-content"></div>
    </ScrollPanel>

    <TemplateDialog v-if="template || showTemplateDialog" entityKey="report_template_id" @refreshRow="refreshRow"
      @addNewRow="addNewRow" v-model:showRowDialog="showTemplateDialog" :rowId="rowId" :permissions="{ writer: true }"
      :masterDetail="{ report_id: reportId }" :dialogOptions="{ numberTemplates: template.length }" />
    <Dialog v-model:visible="deleteDialog" :style="{ width: '550px' }" modal>
      <template #header>
        <i class="pi pi-question-circle mr-3" style="font-size: 2rem; color: rgb(204,204,0)" />
        <div class="col-10 font-bold text-center" style="font-size: 1.3rem">
          Подтверждение действий
        </div>
      </template>

      <div class="confirmation-content">
        <div class="text-center">
          <span v-if="template" :style="{ 'font-size': '1.1rem' }">
            Удалить запись:
            <b>{{ template[active]?.report_template_name }}</b>?
          </span>
        </div>
      </div>
      <template #footer>
        <Button label="Нет" icon="pi pi-times" class="p-button-text" @click="deleteDialog = false" />
        <Button label="Да" icon="pi pi-check" class="p-button-text" @click="deleteRow" />
      </template>
    </Dialog>
  </div>
</template>

<script setup>
import { ref, computed, onUnmounted, watch, defineAsyncComponent } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useToast } from 'primevue/usetoast'
import { formatString } from '../utils'

const router = useRouter();
const route = useRoute();
const store = useStore();
const toast = useToast();

const loading = ref(true);
const template = ref(null);
const active = ref(-1);
const reportId = ref(-1);
const rowId = ref(null);
const showTemplateDialog = ref(false);
const deleteDialog = ref(false);
const reportTemplateParams = ref({});
let viewerInstance = null;

const config = store.getters.config('reportViewer');
const COMPONENT_NAME = 'ReportViewer';

const templateService = store.getters.nameToService(
  config.serviceName,
  store.getters.info.login,
  store.getters.password,
  config.entityName,
  config.entitySpec
);
const logReportService = store.getters.nameToService(
  'BaseService',
  store.getters.info.login,
  store.getters.password,
  'log_report'
);

const handleError = (error, methodName) => {
  store.commit('SET_ERROR', {
    er: error?.response ?? error,
    source: [COMPONENT_NAME, methodName]
  });
}

const destroyViewer = () => {
  if (viewerInstance) {
    const container = document.getElementById('viewer-content')
    if (container) container.innerHTML = '';
    viewerInstance = null;
  }
}

const scrollHeight = computed(() => {
  return window.innerHeight - (active.value < 0 ? 200 : 120)
})

const addNewTemplate = () => {
  rowId.value = undefined
  showTemplateDialog.value = true
}

const editTemplate = () => {
  rowId.value = template.value[active.value].report_template_id
  showTemplateDialog.value = true
}

const refreshRow = () => {
  templateService
    .getData({ report_id: reportId.value })
    .then((data) => {
      template.value = data
    })
    .catch((er) => handleError(er, 'refreshRow'))
}

const deleteRow = () => {
  const templateId = template.value[active.value].report_template_id;
  templateService
    .deleteData({ report_template_id: templateId })
    .then(() => {
      template.value = template.value.filter(item => item.report_template_id !== templateId)
      active.value = -1
      toast.add({
        severity: 'success',
        summary: 'Шаблон удалён',
        detail: 'Запись отчёта успешно удалена из системы',
        life: 3000
      })
      destroyViewer()
    })
    .catch((er) => handleError(er, 'deleteRow'))
    .finally(() => {
      deleteDialog.value = false;
    })
}

const addNewRow = (value) => {
  loading.value = true;
  templateService
    .getData({
      report_id: reportId.value,
      report_template_id: value.report_template_id
    })
    .then((data) => {
      template.value.push(data[0]);
      onClickMenu(template.value.length - 1);
    })
    .catch((er) => handleError(er, 'addNewRow'))
    .finally(() => {
      loading.value = false;
    })
}

const prepareTemplateMenu = () => {
  reportId.value = route.params.id;

  templateService
    .getData({ report_id: reportId.value, report_template_id: 0, show_only_work: true })
    .then((data) => {
      template.value = data;
      active.value = null;
      onClickMenu(0);
    })
    .catch((er) => handleError(er, 'prepareTemplateMenu'))
    .finally(() => {
      loading.value = loading.value ? false : loading.value;
    })
}

const onClickMenu = (index) => {
  if (active.value === index) return;
  active.value = index;
  onShowReport();
}

const onDesignReport = () => {
  const resolved = router.resolve({
    name: 'report-designer',
    params: { id: template.value[active.value].report_template_id }
  });
  window.open(resolved.href, '_blank');
}

const onReportRendered = () => {
  logReportService.insertData({
    report_id: reportId.value,
    report_template_id: template.value[active.value].report_template_id,
    log_report_user: store.getters.info.login,
    log_report_command: JSON.stringify(reportTemplateParams.value)
  }).catch((er) => handleError(er, 'onReportRendered'));
}

const onEndProcessData = (report) => {
  const params = {};
  report.variables.entries.forEach((value) => {
    if (typeof value.value === 'object') {
      params[value.key] = value.value?.innerDate
        ? store.getters.typeFormat(value.value.innerDate, 'date-time')
        : value.value;
    } else if (!value.key.startsWith('**')) {
      params[value.key] = value.value;
    }
  })
  reportTemplateParams.value = params
}

const onShowReport = () => {
  if (!template.value || template.value.length === 0) {
    active.value = -1;
    destroyViewer();
    return;
  }

  destroyViewer();

  const report = window.Stimulsoft.Report.StiReport.createNewReport();
  const data = template.value[active.value].report_template_data;
  if (data) report.load(data);

  const options = new window.Stimulsoft.Viewer.StiViewerOptions();
  for (const item of report.dictionary.databases.list) {
    item.connectionString = formatString(
      store.getters.connectionPath,
      store.getters.info.login,
      store.getters.password
    )
  }
  if (store.getters.info.isAdmin) options.toolbar.showDesignButton = true;
  options.toolbar.showAboutButton = false;

  const viewer = new window.Stimulsoft.Viewer.StiViewer(options, 'StiViewer', false);
  viewer.onDesignReport = () => onDesignReport();
  report.onEndRender = () => onReportRendered();
  report.onEndProcessData = ({ report, result }) => onEndProcessData(report, result)

  viewer.report = report;
  viewer.renderHtml('viewer-content');
  viewerInstance = viewer;

  document.title = template.value[active.value].report_template_name;
}

watch(
  () => ({ id: route.params.id, name: route.name }),
  (newVal, oldVal) => {
    if (newVal.name === 'report-viewer' && newVal.id !== oldVal?.id) {
      loading.value = true;
      prepareTemplateMenu();
    }
  },
  { immediate: true }
)

onUnmounted(() => {
  destroyViewer();
})

const TemplateDialog = defineAsyncComponent(() =>
  import('@/pages/report/TemplateDialog.vue')
)
</script>

<style scoped lang="scss">
::v-deep(.tabmenudemo-content) {
  padding: 2rem 1rem;
}

.vertical-line {
  border-left: 2px solid;
  color: var(--text-color-secondary);
}
</style>